import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to save and restore scroll position for different pages
 * @param key - Unique identifier for the page/component (e.g., page filename)
 * @returns Object with methods to save, get, and restore scroll position
 */
export const useScrollPosition = (key: string) => {
  const isRestoring = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const currentKey = useRef(key);

  // Update the key ref when it changes
  useEffect(() => {
    currentKey.current = key;
  }, [key]);

  const saveScrollPosition = useCallback((position: number) => {
    const storageKey = 'eitum-scroll-positions';
    const stored = localStorage.getItem(storageKey);
    let positions: Record<string, number> = {};
    
    if (stored) {
      try {
        positions = JSON.parse(stored);
      } catch {
        positions = {};
      }
    }
    
    positions[currentKey.current] = position;
    localStorage.setItem(storageKey, JSON.stringify(positions));
  }, []);

  const getScrollPosition = useCallback((): number => {
    const storageKey = 'eitum-scroll-positions';
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed[currentKey.current] || 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }, []);

  const restoreScrollPosition = useCallback(() => {
    const storageKey = 'eitum-scroll-positions';
    const stored = localStorage.getItem(storageKey);
    let position = 0;
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        position = parsed[currentKey.current] || 0;
      } catch {
        position = 0;
      }
    }
    
    if (position > 0) {
      isRestoring.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: position,
            behavior: 'auto'
          });
          requestAnimationFrame(() => {
            isRestoring.current = false;
          });
        });
      });
    }
  }, []);

  // Create a stable scroll handler that doesn't depend on reactive values
  const handleScroll = useCallback(() => {
    if (isRestoring.current) {
      return;
    }
    
    const position = window.scrollY;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      if (position > 10) {
        const storageKey = 'eitum-scroll-positions';
        const stored = localStorage.getItem(storageKey);
        let positions: Record<string, number> = {};
        
        if (stored) {
          try {
            positions = JSON.parse(stored);
          } catch {
            positions = {};
          }
        }
        
        positions[currentKey.current] = position;
        localStorage.setItem(storageKey, JSON.stringify(positions));
      }
    }, 150);
  }, []); // No dependencies!

  useEffect(() => {
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  return {
    saveScrollPosition,
    getScrollPosition,
    restoreScrollPosition
  };
};
